import React, { useMemo, useCallback, useRef, useState } from 'react';
import { Component } from '@/types';
import { useComponentInteraction } from '@/components/shared/rendering/InteractionContextRuntime';
import { hsbToRgb, rgbToHsb, hexToRgb, rgbToHex } from '@/utils/colorUtils';
import { CONTROL_THEME } from './controlThemeTokens';

function kelvinToRgb(kelvin: number): [number, number, number] {
  const temp = kelvin / 100;
  let r: number, g: number, b: number;
  if (temp <= 66) {
    r = 255;
    g = Math.round(99.4708025861 * Math.log(temp) - 161.1195681661);
    b = temp <= 19 ? 0 : Math.round(138.5177312231 * Math.log(temp - 10) - 305.0447927307);
  } else {
    r = Math.round(329.698727446 * Math.pow(temp - 60, -0.1332047592));
    g = Math.round(288.1221695283 * Math.pow(temp - 60, -0.0755148492));
    b = 255;
  }
  return [
    Math.max(0, Math.min(255, r)),
    Math.max(0, Math.min(255, g)),
    Math.max(0, Math.min(255, b)),
  ];
}

// ─── Types ───────────────────────────────────────────────────────────────────

interface ColorPickerThemeConfig {
  bgColor: string;
  borderColor: string;
  textColor: string;
  showHex: boolean;
  showRGB: boolean;
  showHSB: boolean;
  showAlpha: boolean;
  showBrightness: boolean;
  glow: boolean;
}

interface ColorPickerProps {
  component: Component;
}

// ─── Theme Presets ───────────────────────────────────────────────────────────

const THEMES: Record<string, ColorPickerThemeConfig> = {
  default: {
    bgColor: CONTROL_THEME.card,
    borderColor: CONTROL_THEME.border,
    textColor: CONTROL_THEME.foreground,
    showHex: true,
    showRGB: false,
    showHSB: false,
    showAlpha: false,
    showBrightness: true,
    glow: false,
  },
  compact: {
    bgColor: CONTROL_THEME.card,
    borderColor: CONTROL_THEME.border,
    textColor: CONTROL_THEME.foreground,
    showHex: true,
    showRGB: false,
    showHSB: false,
    showAlpha: false,
    showBrightness: false,
    glow: false,
  },
  neon: {
    bgColor: '#1A1A2E',
    borderColor: '#00FF8830',
    textColor: '#cccccc',
    showHex: true,
    showRGB: false,
    showHSB: false,
    showAlpha: false,
    showBrightness: true,
    glow: true,
  },
  minimal: {
    bgColor: 'transparent',
    borderColor: 'transparent',
    textColor: CONTROL_THEME.mutedForeground,
    showHex: false,
    showRGB: false,
    showHSB: false,
    showAlpha: false,
    showBrightness: false,
    glow: false,
  },
  material: {
    bgColor: CONTROL_THEME.card,
    borderColor: CONTROL_THEME.border,
    textColor: CONTROL_THEME.foreground,
    showHex: true,
    showRGB: true,
    showHSB: false,
    showAlpha: false,
    showBrightness: true,
    glow: false,
  },
};

const DEFAULT_PALETTE = [
  '#FF0000', '#FF6600', '#FFCC00', '#00CC00', '#0066FF', '#9900CC',
  '#FF3366', '#FF9933', '#CCFF00', '#00CCCC', '#3366FF', '#CC00CC',
  '#FFFFFF', '#CCCCCC', '#999999', '#666666', '#333333', '#000000',
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getStringValue(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function getBooleanValue(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

function getNumberValue(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function getStringArrayValue(value: unknown): string[] | undefined {
  return Array.isArray(value) && value.every((item) => typeof item === 'string')
    ? value
    : undefined;
}

function getPositivePixelValue(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value;
  }
  if (typeof value !== 'string') {
    return null;
  }
  const match = value.trim().match(/^(\d+(?:\.\d+)?)(?:px)?$/);
  if (!match) {
    return null;
  }
  const parsed = Number(match[1]);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

// ─── Sub-Components ──────────────────────────────────────────────────────────

const ColorWheel: React.FC<{
  hue: number;
  saturation: number;
  size: number;
  isInteractive: boolean;
  onDrag: (hue: number, saturation: number) => void;
}> = ({ hue, saturation, size, isInteractive, onDrag }) => {
  const wheelRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);

  const computeHS = useCallback((clientX: number, clientY: number) => {
    const el = wheelRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = clientX - cx;
    const dy = clientY - cy;
    const angle = (Math.atan2(dy, dx) * 180 / Math.PI + 360) % 360;
    const dist = Math.min(Math.sqrt(dx * dx + dy * dy) / (rect.width / 2), 1);
    onDrag(angle, dist * 100);
  }, [onDrag]);

  const handleDown = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!isInteractive) return;
    e.preventDefault();
    setDragging(true);
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    computeHS(clientX, clientY);
  }, [isInteractive, computeHS]);

  const handleMove = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!dragging) return;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    computeHS(clientX, clientY);
  }, [dragging, computeHS]);

  const handleUp = useCallback(() => setDragging(false), []);

  // Calculate indicator position
  const angle = hue * (Math.PI / 180);
  const dist = (saturation / 100) * (size / 2);
  const indicatorX = size / 2 + Math.cos(angle) * dist;
  const indicatorY = size / 2 + Math.sin(angle) * dist;

  return (
    <div
      ref={wheelRef}
      data-testid="color-wheel"
      onMouseDown={handleDown}
      onMouseMove={handleMove}
      onMouseUp={handleUp}
      onMouseLeave={handleUp}
      onTouchStart={handleDown}
      onTouchMove={handleMove}
      onTouchEnd={handleUp}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: `conic-gradient(from 90deg,
          hsl(0, 100%, 50%),
          hsl(60, 100%, 50%),
          hsl(120, 100%, 50%),
          hsl(180, 100%, 50%),
          hsl(240, 100%, 50%),
          hsl(300, 100%, 50%),
          hsl(360, 100%, 50%)
        )`,
        position: 'relative',
        cursor: isInteractive ? 'crosshair' : 'default',
        flexShrink: 0,
      }}
    >
      {/* Saturation overlay: white center → transparent edge */}
      <div style={{
        position: 'absolute',
        inset: 0,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 70%)',
      }} />
      {/* Selection indicator */}
      <div style={{
        position: 'absolute',
        left: indicatorX - 6,
        top: indicatorY - 6,
        width: 12,
        height: 12,
        borderRadius: '50%',
        border: '2px solid white',
        boxShadow: '0 0 3px rgba(0,0,0,0.5)',
        pointerEvents: 'none',
      }} />
    </div>
  );
};

const ColorStrip: React.FC<{
  hue: number;
  size: number;
  isInteractive: boolean;
  onDrag: (hue: number) => void;
}> = ({ hue, size, isInteractive, onDrag }) => {
  const stripRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);

  const computeH = useCallback((clientX: number) => {
    const el = stripRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    onDrag(ratio * 360);
  }, [onDrag]);

  const handleDown = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!isInteractive) return;
    e.preventDefault();
    setDragging(true);
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    computeH(clientX);
  }, [isInteractive, computeH]);

  const handleMove = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!dragging) return;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    computeH(clientX);
  }, [dragging, computeH]);

  const handleUp = useCallback(() => setDragging(false), []);

  const pct = (hue / 360) * 100;

  return (
    <div
      ref={stripRef}
      data-testid="color-strip"
      onMouseDown={handleDown}
      onMouseMove={handleMove}
      onMouseUp={handleUp}
      onMouseLeave={handleUp}
      onTouchStart={handleDown}
      onTouchMove={handleMove}
      onTouchEnd={handleUp}
      style={{
        width: size,
        height: 20,
        borderRadius: 4,
        background: `linear-gradient(to right,
          hsl(0, 100%, 50%),
          hsl(60, 100%, 50%),
          hsl(120, 100%, 50%),
          hsl(180, 100%, 50%),
          hsl(240, 100%, 50%),
          hsl(300, 100%, 50%),
          hsl(360, 100%, 50%)
        )`,
        position: 'relative',
        cursor: isInteractive ? 'pointer' : 'default',
        flexShrink: 0,
      }}
    >
      <div style={{
        position: 'absolute',
        left: `${pct}%`,
        top: -2,
        width: 4,
        height: 24,
        backgroundColor: 'white',
        border: '1px solid rgba(0,0,0,0.3)',
        borderRadius: 2,
        transform: 'translateX(-50%)',
        pointerEvents: 'none',
      }} />
    </div>
  );
};

const ColorPalette: React.FC<{
  colors: string[];
  currentColor: string;
  isInteractive: boolean;
  onSelect: (color: string) => void;
}> = ({ colors, currentColor, isInteractive, onSelect }) => (
  <div
    data-testid="color-palette"
    style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(6, 1fr)',
      gap: 4,
      width: '100%',
    }}
  >
    {colors.map((color, i) => (
      <button
        key={`${color}-${i}`}
        data-testid={`palette-color-${i}`}
        onClick={() => isInteractive && onSelect(color)}
        style={{
          width: '100%',
          aspectRatio: '1',
          backgroundColor: color,
          border: color.toUpperCase() === currentColor.toUpperCase()
            ? `2px solid ${CONTROL_THEME.foreground}`
            : `1px solid ${CONTROL_THEME.border}`,
          borderRadius: 4,
          cursor: isInteractive ? 'pointer' : 'default',
          outline: 'none',
          padding: 0,
        }}
        aria-label={`Select ${color}`}
      />
    ))}
  </div>
);

const BrightnessSlider: React.FC<{
  brightness: number;
  hue: number;
  saturation: number;
  width: number;
  isInteractive: boolean;
  onChange: (b: number) => void;
}> = ({ brightness, hue, saturation, width, isInteractive, onChange }) => {
  const sliderRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);

  const computeB = useCallback((clientX: number) => {
    const el = sliderRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    onChange(Math.round(ratio * 100));
  }, [onChange]);

  const handleDown = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!isInteractive) return;
    e.preventDefault();
    setDragging(true);
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    computeB(clientX);
  }, [isInteractive, computeB]);

  const handleMove = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!dragging) return;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    computeB(clientX);
  }, [dragging, computeB]);

  const handleUp = useCallback(() => setDragging(false), []);

  const [r, g, b] = hsbToRgb(hue, saturation, 100);
  const fullColor = `rgb(${r},${g},${b})`;
  const pct = brightness;

  return (
    <div
      ref={sliderRef}
      data-testid="brightness-slider"
      onMouseDown={handleDown}
      onMouseMove={handleMove}
      onMouseUp={handleUp}
      onMouseLeave={handleUp}
      onTouchStart={handleDown}
      onTouchMove={handleMove}
      onTouchEnd={handleUp}
      style={{
        width,
        height: 14,
        borderRadius: 7,
        background: `linear-gradient(to right, #000000, ${fullColor})`,
        position: 'relative',
        cursor: isInteractive ? 'pointer' : 'default',
        flexShrink: 0,
      }}
    >
      <div style={{
        position: 'absolute',
        left: `${pct}%`,
        top: -1,
        width: 8,
        height: 16,
        backgroundColor: 'white',
        border: '1px solid rgba(0,0,0,0.3)',
        borderRadius: 4,
        transform: 'translateX(-50%)',
        pointerEvents: 'none',
      }} />
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

export const ColorPicker: React.FC<ColorPickerProps> = ({ component }) => {
  const properties = component.properties ?? {};

  // ─── Property Extraction ─────────────────────────────────────────────
  const themeName = getStringValue(properties.theme) ?? 'default';
  const pickerMode = getStringValue(properties.mode) ?? 'wheel';
  const currentColor = getStringValue(properties.color) ?? '#FF0000';
  const presetColors = getStringArrayValue(properties.presetColors) ?? DEFAULT_PALETTE;

  // ─── Theme Resolution ────────────────────────────────────────────────
  const colorTemperature = getNumberValue(properties.colorTemperature);

  const theme = useMemo(() => {
    const base = THEMES[themeName] ?? THEMES['default'];
    return {
      ...base,
      showHex: getBooleanValue(properties.showHex) ?? base.showHex,
      showRGB: getBooleanValue(properties.showRGB) ?? base.showRGB,
      showHSB: getBooleanValue(properties.showHSB) ?? base.showHSB,
      showAlpha: getBooleanValue(properties.showAlpha) ?? base.showAlpha,
      showBrightness: getBooleanValue(properties.showBrightness) ?? base.showBrightness,
    };
  }, [themeName, properties.showHex, properties.showRGB, properties.showHSB, properties.showAlpha, properties.showBrightness]);

  // ─── HSB State ───────────────────────────────────────────────────────
  const [h, s, b] = useMemo(() => {
    const [r, g, bVal] = hexToRgb(currentColor);
    return rgbToHsb(r, g, bVal);
  }, [currentColor]);

  // ─── Interaction ─────────────────────────────────────────────────────
  const { handlers, mode } = useComponentInteraction(component);
  const isViewerMode = mode === 'functional';

  const sensorId = useMemo(() => {
    const action = properties.action;
    if (isRecord(action)) {
      return getStringValue(action.deviceId) ?? component.id;
    }
    return component.id;
  }, [properties.action, component.id]);

  const fireColorChange = useCallback((hex: string) => {
    if (!isViewerMode) return;
    handlers.onSensorControl?.({
      sensorId,
      action: 'control',
      parameters: { command: 'set', value: hex, property: 'color' },
    });
  }, [isViewerMode, handlers, sensorId]);

  const handleWheelDrag = useCallback((hue: number, saturation: number) => {
    const [r, g, bVal] = hsbToRgb(hue, saturation, b);
    fireColorChange(rgbToHex(r, g, bVal));
  }, [b, fireColorChange]);

  const handleStripDrag = useCallback((hue: number) => {
    const [r, g, bVal] = hsbToRgb(hue, s, b);
    fireColorChange(rgbToHex(r, g, bVal));
  }, [s, b, fireColorChange]);

  const handleBrightnessChange = useCallback((brightness: number) => {
    const [r, g, bVal] = hsbToRgb(h, s, brightness);
    fireColorChange(rgbToHex(r, g, bVal));
  }, [h, s, fireColorChange]);

  const handlePaletteSelect = useCallback((color: string) => {
    fireColorChange(color);
  }, [fireColorChange]);

  // ─── Computed ────────────────────────────────────────────────────────
  const effectiveColor = useMemo(() => {
    if (colorTemperature != null && colorTemperature >= 2700 && colorTemperature <= 6500) {
      const [r, g, bVal] = kelvinToRgb(colorTemperature);
      return rgbToHex(r, g, bVal);
    }
    return currentColor;
  }, [currentColor, colorTemperature]);

  const [cr, cg, cb] = hexToRgb(effectiveColor);
  const [ch, cs, cBright] = rgbToHsb(cr, cg, cb);
  const containerWidth = getPositivePixelValue(properties.containerWidth)
    ?? getPositivePixelValue(component.size?.width)
    ?? 200;
  const containerHeight = getPositivePixelValue(component.size?.height) ?? 200;
  const hasReadout = theme.showHex || theme.showRGB || theme.showHSB || theme.showAlpha || colorTemperature != null;
  const reservedHeight = 48
    + (hasReadout ? 18 : 0)
    + (colorTemperature != null ? 16 : 0);
  const pickerSize = Math.max(72, Math.min(containerWidth - 16, containerHeight - reservedHeight, 160));
  const controlRowWidth = Math.max(80, Math.min(containerWidth - 16, 160));
  const brightnessWidth = Math.max(48, controlRowWidth - 40);

  // ─── Render ──────────────────────────────────────────────────────────
  return (
    <div
      data-testid={`color-picker-${component.id}`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        gap: 8,
        padding: 8,
        boxSizing: 'border-box',
        backgroundColor: theme.bgColor,
        border: `1px solid ${theme.borderColor}`,
        borderRadius: 8,
        minWidth: 0,
        minHeight: 0,
        overflow: 'hidden',
        userSelect: 'none',
      }}
    >
      {/* Picker area */}
      {pickerMode === 'wheel' && (
        <ColorWheel
          hue={h}
          saturation={s}
          size={Math.min(pickerSize - 16, 160)}
          isInteractive={isViewerMode}
          onDrag={handleWheelDrag}
        />
      )}

      {pickerMode === 'strip' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%', alignItems: 'center' }}>
          <ColorStrip
            hue={h}
            size={pickerSize - 16}
            isInteractive={isViewerMode}
            onDrag={handleStripDrag}
          />
        </div>
      )}

      {pickerMode === 'palette' && (
        <ColorPalette
          colors={presetColors}
          currentColor={currentColor}
          isInteractive={isViewerMode}
          onSelect={handlePaletteSelect}
        />
      )}

      <div
        data-testid="color-control-row"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          width: controlRowWidth,
          flexShrink: 0,
        }}
      >
        {/* Color swatch */}
        <div
          data-testid="color-swatch"
          style={{
            width: 32,
            height: 32,
            borderRadius: 6,
            backgroundColor: effectiveColor,
            border: `2px solid ${theme.borderColor}`,
            flexShrink: 0,
            ...(theme.glow ? { boxShadow: `0 0 10px ${effectiveColor}60` } : {}),
          }}
        />

        {/* Brightness slider */}
        {theme.showBrightness && (
          <BrightnessSlider
            brightness={b}
            hue={h}
            saturation={s}
            width={brightnessWidth}
            isInteractive={isViewerMode}
            onChange={handleBrightnessChange}
          />
        )}
      </div>

      {/* Color temperature indicator */}
      {colorTemperature != null && (
        <span style={{ fontSize: 10, color: theme.textColor, fontFamily: 'monospace' }}>
          {colorTemperature}K
        </span>
      )}

      {/* Readouts */}
      <div style={{ display: 'flex', gap: 8, fontSize: 11, color: theme.textColor, fontFamily: 'monospace', flexWrap: 'wrap', justifyContent: 'center', minWidth: 0 }}>
        {theme.showHex && <span>{effectiveColor}</span>}
        {theme.showRGB && <span>rgb({cr},{cg},{cb})</span>}
        {theme.showHSB && <span>hsb({ch}°,{cs}%,{cBright}%)</span>}
        {theme.showAlpha && <span>a:100%</span>}
      </div>
    </div>
  );
};
